import React, { useEffect, useState, useMemo } from 'react';
import { Form, Input, Button, message, Select, Modal, Space, Popconfirm, Tag, Card, Row, Col, Avatar, Radio, Alert } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { usersApi, expertsApi } from '../services/api-services';
import ImagePicker from '../components/ImagePicker';

const { TextArea } = Input;

const NOTIFY_METHODS = [
  { label: '邮件', value: 'email' },
  { label: '腾讯短信', value: 'tencent_sms' },
  { label: 'Webhook', value: 'webhook' },
  { label: '飞书', value: 'feishu' },
  { label: '站内信', value: 'internal' },
];

const NOTIFY_ADDR_META: Record<string, { label: string; placeholder: string }> = {
  email: { label: '邮箱地址', placeholder: 'name@example.com' },
  tencent_sms: { label: '手机号', placeholder: '请输入手机号' },
  webhook: { label: 'Webhook URL', placeholder: 'https://...' },
  feishu: { label: '飞书 ID', placeholder: '飞书用户 ID' },
  internal: { label: '接收人（用户名）', placeholder: '站内信接收用户名' },
};

interface Expert {
  id: number;
  user_id?: number | null;
  name?: string;
  avatar?: string;
  title?: string;
  bio?: string;
  expertise_tags?: string[];
  notification_config?: Record<string, any>;
}

const ExpertList: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [experts, setExperts] = useState<Expert[]>([]);
  const [expertLoading, setExpertLoading] = useState(false);
  const [expertModalVisible, setExpertModalVisible] = useState(false);
  const [editingExpert, setEditingExpert] = useState<Expert | null>(null);
  const [filterTag, setFilterTag] = useState<string | undefined>(undefined);
  const [expertForm] = Form.useForm();
  const [expertLinkMode, setExpertLinkMode] = useState<'none' | 'user'>('none');
  const notifyMethod = Form.useWatch('notify_method', expertForm);

  const loadUsers = async () => {
    try { const res: any = await usersApi.list(); setUsers(res); } catch {}
  };

  const fetchExperts = async () => {
    setExpertLoading(true);
    try { const res: any = await expertsApi.list(); setExperts(res); } catch { message.error('获取专家列表失败'); }
    setExpertLoading(false);
  };

  useEffect(() => { loadUsers(); fetchExperts(); }, []);

  const allTags = useMemo(
    () => Array.from(new Set(experts.flatMap((e) => e.expertise_tags ?? []))),
    [experts],
  );

  const filteredExperts = useMemo(
    () => filterTag ? experts.filter((e) => e.expertise_tags?.includes(filterTag)) : experts,
    [experts, filterTag],
  );

  const handleSaveExpert = async (values: any) => {
    try {
      const payload: any = {
        title: values.title,
        bio: values.bio,
        expertise_tags: values.expertise_tags,
        notification_config: values.notify_method
          ? {
              method: values.notify_method,
              address: values.notify_address || '',
              ...(values.notify_method === 'tencent_sms'
                ? { sms_template_id: values.notify_sms_template_id || '' }
                : {}),
            }
          : {},
      };

      if (editingExpert) {
        if (!editingExpert.user_id) {
          payload.name = values.name;
          payload.avatar = values.avatar;
        }
        await expertsApi.update(editingExpert.id, payload);
        message.success('更新成功');
      } else if (expertLinkMode === 'user') {
        if (!values.linked_user_id) { message.error('请选择要关联的系统用户'); return; }
        payload.user_id = values.linked_user_id;
        await expertsApi.create(payload);
        message.success('关联成功');
      } else {
        payload.name = values.name;
        payload.avatar = values.avatar;
        await expertsApi.create(payload);
        message.success('创建成功');
      }
      setExpertModalVisible(false); expertForm.resetFields(); setEditingExpert(null); setExpertLinkMode('none');
      fetchExperts();
    } catch (e: any) { message.error(e.response?.data?.detail || '操作失败'); }
  };

  const handleDeleteExpert = async (id: number) => {
    try { await expertsApi.delete(id); message.success('删除成功'); fetchExperts(); } catch { message.error('删除失败'); }
  };

  const openEditExpert = (record: Expert) => {
    setEditingExpert(record);
    setExpertLinkMode('none');
    const nc: any = record.notification_config || {};
    expertForm.setFieldsValue({
      ...record,
      notify_method: nc.method,
      notify_address: nc.address,
      notify_sms_template_id: nc.sms_template_id,
    });
    setExpertModalVisible(true);
  };

  return (
    <div className="apple-page">
      <div className="apple-page-header">
        <h1 className="apple-page-title">人工专家</h1>
        <Button className="apple-btn-create" icon={<PlusOutlined />} onClick={() => { setEditingExpert(null); expertForm.resetFields(); setExpertLinkMode('none'); setExpertModalVisible(true); }}>新建专家</Button>
      </div>
      <p className="apple-page-subtitle">管理人工专家信息，配置通知方式与专业领域。</p>

      <div className="apple-toolbar" style={{ marginBottom: 16 }}>
        <Select allowClear placeholder="按专业领域筛选" style={{ width: 260 }} value={filterTag} onChange={setFilterTag}
          options={allTags.map((t) => ({ label: t, value: t }))} />
      </div>

      <Row gutter={[16, 16]}>
        {filteredExperts.map((expert) => (
          <Col xs={24} sm={12} md={8} lg={6} key={expert.id}>
            <Card hoverable loading={expertLoading} className="expert-card" onClick={() => openEditExpert(expert)}
              cover={
                <div className="expert-card-avatar">
                  <Avatar size={72} src={expert.avatar || undefined}
                    style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', fontWeight: 600 }}>
                    {expert.name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                </div>
              }
              actions={[
                <Button key="edit" type="link" size="small" icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); openEditExpert(expert); }}>编辑</Button>,
                <Popconfirm key="del" title="确定删除该专家?" onConfirm={() => handleDeleteExpert(expert.id)} okText="确定" cancelText="取消">
                  <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()}>删除</Button>
                </Popconfirm>,
              ]}
            >
              <Card.Meta title={<span className="expert-card-name">{expert.name}</span>}
                description={
                  <div className="expert-card-body">
                    <div className="expert-card-title-text">{expert.title || '暂无职称'}</div>
                    <div className="expert-card-tags">
                      {expert.expertise_tags && expert.expertise_tags.length > 0
                        ? expert.expertise_tags.slice(0, 3).map((tag) => <Tag key={tag} color="blue" style={{ marginRight: 4, marginBottom: 2 }}>{tag}</Tag>)
                        : <span style={{ color: 'var(--apple-text-secondary)', fontSize: 12 }}>暂无标签</span>}
                      {expert.expertise_tags && expert.expertise_tags.length > 3 && (
                        <Tag style={{ marginRight: 4, marginBottom: 2 }}>+{expert.expertise_tags.length - 3}</Tag>
                      )}
                    </div>
                    <div className="expert-card-bio">{expert.bio || '暂无简介'}</div>
                  </div>
                }
              />
            </Card>
          </Col>
        ))}
      </Row>
      {filteredExperts.length === 0 && !expertLoading && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--apple-text-secondary)' }}>暂无专家数据</div>
      )}

      {/* 新建/编辑专家 Modal */}
      <Modal className="apple-modal" title={editingExpert ? '编辑专家' : '新建专家'} open={expertModalVisible}
        onCancel={() => { setExpertModalVisible(false); expertForm.resetFields(); setEditingExpert(null); setExpertLinkMode('none'); }}
        onOk={() => expertForm.submit()} width={640} destroyOnHidden>
        <Form className="apple-form" form={expertForm} layout="vertical" onFinish={handleSaveExpert}>
          {!editingExpert && (
            <Form.Item label="关联系统用户">
              <Radio.Group value={expertLinkMode} onChange={(e) => setExpertLinkMode(e.target.value)}>
                <Radio value="none">不关联（独立专家）</Radio>
                <Radio value="user">关联系统用户</Radio>
              </Radio.Group>
            </Form.Item>
          )}

          {!editingExpert && expertLinkMode === 'user' && (
            <Form.Item name="linked_user_id" label="选择用户" rules={[{ required: true, message: '请选择要关联的系统用户' }]}
              tooltip="关联后将复用该用户的姓名与头像，并把其标记为专家">
              <Select
                showSearch
                placeholder="选择一个系统用户"
                optionFilterProp="label"
                options={users.filter((u) => !experts.some((e) => e.user_id === u.id)).map((u) => ({ label: `${u.name}（${u.username}）`, value: u.id }))}
              />
            </Form.Item>
          )}

          {editingExpert && editingExpert.user_id && (
            <Form.Item label="关联用户">
              <Input value={editingExpert.name || ''} disabled addonBefore="姓名/头像复用该用户" />
            </Form.Item>
          )}

          {((editingExpert && !editingExpert.user_id) || (!editingExpert && expertLinkMode === 'none')) && (
            <>
              <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}><Input placeholder="请输入姓名" /></Form.Item>
              <Form.Item name="avatar" label="头像"><ImagePicker shape="circle" /></Form.Item>
            </>
          )}

          <Form.Item name="title" label="职称 / 头衔"><Input placeholder="如：主任医师、教授" /></Form.Item>
          <Form.Item name="expertise_tags" label="擅长领域"><Select mode="tags" placeholder="输入后回车添加标签" /></Form.Item>
          <Form.Item name="bio" label="简介"><TextArea rows={4} placeholder="专家简介" /></Form.Item>
          <Form.Item name="notify_method" label="通知方式">
            <Select allowClear placeholder="选择通知方式" options={NOTIFY_METHODS}
              onChange={() => expertForm.setFieldsValue({ notify_address: undefined })} />
          </Form.Item>
          {notifyMethod && (
            <Form.Item
              name="notify_address"
              label={notifyMethod === 'internal' ? '接收人' : (NOTIFY_ADDR_META[notifyMethod]?.label || '地址')}
              rules={[{ required: true, message: `请${notifyMethod === 'internal' ? '选择接收人' : `填写${NOTIFY_ADDR_META[notifyMethod]?.label || '地址'}`}` }]}
            >
              {notifyMethod === 'internal' ? (
                <Select
                  showSearch
                  placeholder="选择站内信接收人"
                  optionFilterProp="label"
                  options={users.map((u) => ({ label: `${u.name}（${u.username}）`, value: u.id }))}
                />
              ) : (
                <Input placeholder={NOTIFY_ADDR_META[notifyMethod]?.placeholder} />
              )}
            </Form.Item>
          )}
          {notifyMethod === 'webhook' && (
            <Alert
              message="Webhook 推送说明"
              description={
                <div>
                  系统将通过 HTTP POST 请求发送工单信息到指定 URL
                  <div style={{ marginTop: 8 }}>
                    <strong>请求格式：</strong>
                    <pre style={{ marginTop: 4, padding: 8, background: '#f5f5f5', borderRadius: 4, fontSize: 12 }}>
{`POST {你的URL}
Content-Type: application/json

{
  "type": "ticket",
  "subject": "工单",
  "content": "TK-xxx - 问题描述...",
  "timestamp": "2026-06-10T11:20:30",
  "data": {
    "ticket_id": 123,
    "task_id": "TK-xxx",
    "category": "技术问题",
    "problem_description": "完整描述",
    "expert_id": 6,
    "expert_name": "张医生",
    "created_at": "2026-06-10T11:20:30"
  }
}`}
                    </pre>
                  </div>
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          {notifyMethod === 'internal' && (
            <Alert
              message="站内信通知说明"
              description="工单创建后，系统会在平台内向指定用户发送站内消息通知，用户登录后可在通知中心查看。"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          {notifyMethod === 'tencent_sms' && (
            <>
              <Form.Item
                name="notify_sms_template_id"
                label="短信模板ID"
                tooltip="该专家短信通知所使用的腾讯云短信模板 ID"
                rules={[{ required: true, message: '请填写短信模板ID' }]}
              >
                <Input placeholder="如 1234567" />
              </Form.Item>
              <Alert
                message="短信模板参数说明"
                description={
                  <div>
                    在腾讯云创建短信模板时，需包含两个参数：
                    <div style={{ marginTop: 8 }}>
                      <code>{'{1}'}</code> - 类型（如：工单）<br />
                      <code>{'{2}'}</code> - 内容（具体信息）
                    </div>
                    <div style={{ marginTop: 8, color: '#666' }}>
                      示例模板：您有一条【{'{1}'}】待处理，内容：【{'{2}'}】。
                    </div>
                  </div>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default ExpertList;
